import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { GoogleLogin, CredentialResponse } from '@react-oauth/google'
import { useAuthStore } from '../../store/auth'
import { authApi } from '@eshop/shared'
import { useLanguage } from '@eshop/shared'

type LoginMethod = 'google' | 'email'

export function Login() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const { setAuth, isAuthenticated } = useAuthStore()
  const [loginMethod, setLoginMethod] = useState<LoginMethod | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const handleGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    try {
      if (!credentialResponse.credential) {
        console.error('No credential received from Google')
        return
      }

      const response = await authApi.googleLogin(credentialResponse.credential)
      setAuth(response.user, response.tokens)
      navigate('/dashboard')
    } catch (error) {
      console.error('Login failed:', error)
      setError(t('loginFailed'))
    }
  }

  const handleGoogleError = () => {
    console.error('Google login failed')
    setError(t('googleLoginFailed'))
  }

  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const response = await authApi.emailLogin(email, password)
      setAuth(response.user, response.tokens)
      navigate('/dashboard')
    } catch (err: any) {
      console.error('Email login failed:', err)
      setError(err.response?.data?.non_field_errors?.[0] || t('invalidCredentials'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
        <h1 className="text-3xl font-bold text-center mb-2 text-gray-900">
          {t('backofficeTitle')}
        </h1>
        <p className="text-center text-gray-600 mb-8">
          {t('loginSubtitle')}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
            {error}
          </div>
        )}

        {!loginMethod ? (
          <div className="space-y-3">
            <button
              onClick={() => setLoginMethod('google')}
              className="w-full px-4 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              {t('continueWithGoogle')}
            </button>

            <button
              onClick={() => setLoginMethod('email')}
              className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              {t('continueWithEmail')}
            </button>

            <div className="text-center mt-4">
              <button
                onClick={() => navigate('/register')}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                {t('noAccount')} <span className="font-semibold">{t('createAccount')}</span>
              </button>
            </div>
          </div>
        ) : loginMethod === 'google' ? (
          <div className="space-y-4">
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="filled_blue"
                size="large"
                text="signin_with"
                shape="rectangular"
              />
            </div>

            <button
              onClick={() => {
                setLoginMethod(null)
                setError('')
              }}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              ← {t('backToOptions')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('email')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('emailPlaceholder')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('password')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={t('passwordPlaceholder')}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:bg-blue-400"
              >
                {loading ? t('loggingIn') : t('login')}
              </button>
            </form>

            <button
              onClick={() => {
                setLoginMethod(null)
                setError('')
                setEmail('')
                setPassword('')
              }}
              className="w-full px-4 py-2 text-gray-600 hover:text-gray-800 text-sm"
            >
              ← {t('backToOptions')}
            </button>

            <div className="text-center">
              <button
                onClick={() => navigate('/register')}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                {t('noAccount')} <span className="font-semibold">{t('createAccount')}</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
